import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

interface CacheConfig {
  tier: 'static' | 'content' | 'dynamic' | 'realtime' | 'updatable';
}

const CACHE_TIERS = {
  // Static pages (Settings, Widgets, Holidays) - never changes
  static: {
    staleTime: Infinity,
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
    retry: 1,
  },
  // Content pages (Articles, Places) - change every few days
  content: {
    staleTime: 3 * 24 * 60 * 60 * 1000, // 3 days
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    retry: 1,
  },
  // Dynamic data (Weather, Events) - changes daily/hourly
  dynamic: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 1,
  },
  // Real-time data (Currency rates) - changes frequently
  realtime: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  },
  // Updatable content (Articles, Categories, Places) - irregular updates, 1 hour freshness
  updatable: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 1,
  },
};

export function useOptimizedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData> & CacheConfig,
): UseQueryResult<TData, TError> {
  const { tier, ...queryOptions } = options;

  const tier_config = CACHE_TIERS[tier];

  return useQuery<TQueryFnData, TError, TData>({
    ...queryOptions,
    staleTime: queryOptions.staleTime ?? tier_config.staleTime,
    gcTime: queryOptions.gcTime ?? tier_config.gcTime,
    retry: queryOptions.retry ?? tier_config.retry,
  });
}

export const CACHE_TIERS_CONFIG = CACHE_TIERS;
